/**
 * Employees report — migrated to the generic `CrudPage` for the table
 * surface (column filtering, sort, tabs, bulk select, right-click menu,
 * pagination, search). The page itself owns the create/edit form Card
 * and the Assign Department dialog so we don't have to translate every
 * quirk of the bespoke form (password masking, branches multi-select
 * with checkbox grid, portal-login gating) into generic FieldConfig
 * primitives. `editOverride` / `createOverride` on the CrudConfig let
 * the page intercept those triggers and open its own UI instead of the
 * generic inline edit form.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/toast';
import { useUserActions } from '@/hooks/use-user-actions';
import { useConfirm } from '@/hooks/use-confirm';
import { CrudPage, type CrudConfig, type ColumnConfig } from '@/components/shared/crud-page';
import { LookupField } from '@/components/shared/lookup-field';

function useList<T = any>(url: string) {
  return useQuery({
    queryKey: ['list', url],
    queryFn: async () => (await api<T[]>(url)).data || [],
  });
}

const BLANK = {
  name: { prefix: '', firstName: '', lastName: '', suffix: '' },
  email: '',
  mobile: '',
  // Password intentionally absent — admins no longer set passwords from
  // this form. New hires receive a portal-invitation email containing a
  // password-setup link; existing users change their password from the
  // Profile page. Removing the field also strips it from `createEmployeeSchema`
  // server-side, so even a hand-crafted request can't reach the bcrypt path.
  hourlyRate: 0,
  createPortalLogin: false,
  jobcardNotificationEmail: false,
  emailTipperFleetRunSheet: false,
  department: null,
  rolePermission: null,
  branches: [] as string[],
};

export function EmployeesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<any>(null);
  const [form, setForm] = React.useState<any>(BLANK);
  const [assignDeptDialog, setAssignDeptDialog] = React.useState<{ ids: string[] } | null>(null);
  const [pickedDeptId, setPickedDeptId] = React.useState<string>('');
  const { actions: grantedActions } = useUserActions();
  const canInvitePortal = grantedActions.has('employee.invite-portal');

  const { data: roles = [] } = useList<any>('/roles?limit=200');
  const { data: departments = [] } = useList<any>('/master-data/departments?limit=200');
  const { data: branches = [] } = useList<any>('/master-data/branches?limit=200');
  const { data: serviceLocations = [] } = useList<any>('/master-data/service-locations?limit=200');

  // ---- Mutations (called from the custom form / dialogs / row actions) ----
  const save = useMutation({
    mutationFn: async (body: any) => {
      const url = editing ? `/employees/${editing.id}` : '/employees';
      const method = editing ? 'PUT' : 'POST';
      const r = await api(url, { method, body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast(editing ? 'Employee updated' : 'Employee created', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/employees'] });
      setOpen(false);
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const invitePortal = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/employees/${id}/invite-portal`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Portal login enabled', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/employees'] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const bulkInvitePortal = useMutation({
    mutationFn: async (ids: string[]) => {
      const r = await api<any>('/employees/bulk-invite-portal', { method: 'POST', body: JSON.stringify({ ids }) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: (data: any) => {
      const enabled = data?.enabled ?? 0;
      const skipped = data?.skipped?.length ?? 0;
      toast('Portal login', `Enabled on ${enabled}${skipped ? `, skipped ${skipped}` : ''}.`, 'success');
      qc.invalidateQueries({ queryKey: ['/employees'] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const assignDept = useMutation({
    mutationFn: async ({ ids, departmentId }: { ids: string[]; departmentId: string }) => {
      const url = ids.length === 1 ? `/employees/${ids[0]}/assign-department` : '/employees/bulk-assign-department';
      const body = ids.length === 1 ? { departmentId } : { ids, departmentId };
      const r = await api<any>(url, { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: (_data, vars) => {
      toast('Department assigned', `${vars.ids.length} employee(s) updated`, 'success');
      setAssignDeptDialog(null);
      setPickedDeptId('');
      qc.invalidateQueries({ queryKey: ['/employees'] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const unassignDept = useMutation({
    mutationFn: async (ids: string[]) => {
      const url = ids.length === 1 ? `/employees/${ids[0]}/unassign-department` : '/employees/bulk-unassign-department';
      const body = ids.length === 1 ? undefined : JSON.stringify({ ids });
      const r = await api<any>(url, { method: 'POST', body });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: (_data, ids) => {
      toast('Department cleared', `${ids.length} employee(s) updated`, 'success');
      qc.invalidateQueries({ queryKey: ['/employees'] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const deactivate = useMutation({
    mutationFn: async (ids: string[]) => {
      const url = ids.length === 1 ? `/employees/${ids[0]}/deactivate` : '/employees/bulk-deactivate';
      const body = ids.length === 1 ? undefined : JSON.stringify({ ids });
      const r = await api<any>(url, { method: 'POST', body });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: (data: any, ids) => {
      const n = data?.deactivated ?? ids.length;
      const skipped = data?.skipped ?? 0;
      toast('Deactivated', `${n} employee(s) archived${skipped ? `, skipped ${skipped} without portal login` : ''}.`, 'success');
      qc.invalidateQueries({ queryKey: ['/employees'] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const reactivate = useMutation({
    mutationFn: async (ids: string[]) => {
      const url = ids.length === 1 ? `/employees/${ids[0]}/reactivate` : '/employees/bulk-reactivate';
      const body = ids.length === 1 ? undefined : JSON.stringify({ ids });
      const r = await api<any>(url, { method: 'POST', body });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: (data: any, ids) => {
      const n = data?.reactivated ?? ids.length;
      const skipped = data?.skipped ?? 0;
      toast('Reactivated', `${n} employee(s) set Active${skipped ? `, ${skipped} already active` : ''}.`, 'success');
      qc.invalidateQueries({ queryKey: ['/employees'] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  // ---- Form helpers ----
  function openNew() {
    setEditing(null);
    setForm(BLANK);
    setOpen(true);
  }
  // Helper — server populates refs as `{ id, ... }` objects; zod expects
  // the bare ObjectId string on save. Strip arrays + scalars uniformly so a
  // new populate path doesn't silently re-break the form.
  function refToId(v: any): string | null {
    if (!v) return null;
    if (typeof v === 'string') return v;
    return v.id ?? v._id ?? null;
  }
  function refsToIds(arr: any): string[] {
    return (Array.isArray(arr) ? arr : []).map(refToId).filter((x): x is string => !!x);
  }
  function openEdit(row: any) {
    setEditing(row);
    setForm({
      ...row,
      department:      refToId(row.department),
      rolePermission:  refToId(row.rolePermission),
      serviceLocation: refToId(row.serviceLocation),
      branches:        refsToIds(row.branches),
    });
    setOpen(true);
  }
  function submit() {
    const body: any = { ...form };
    // Defensive — even though the form no longer has a password input, a
    // populated `password` could still slip through if the field is added
    // back later. Drop it before sending so admins can never bypass the
    // invitation / Profile-page-only password flow.
    delete body.password;
    if (!body.mobile) delete body.mobile;
    if (!body.email || !String(body.email).trim()) body.email = null;
    if (body.createPortalLogin && (!body.email || !canInvitePortal)) delete body.createPortalLogin;
    save.mutate(body);
  }
  function toggleBranch(id: string) {
    setForm((f: any) => {
      const has = f.branches.includes(id);
      return { ...f, branches: has ? f.branches.filter((x: string) => x !== id) : [...f.branches, id] };
    });
  }

  // ---- CrudPage config ----
  const columns: ColumnConfig[] = [
    { key: 'fullName', label: 'Name', filterType: 'text' },
    { key: 'email', label: 'Email', filterType: 'text', render: (r: any) => r.email || '—' },
    { key: 'rolePermission.name', label: 'Role',
      filterType: 'ref', filterField: 'rolePermission',
      refEndpoint: '/roles', refLabel: 'name',
      sortField: 'rolePermission',
      render: (r: any) => r.rolePermission?.name || '—' },
    { key: 'department.name', label: 'Department',
      filterType: 'ref', filterField: 'department',
      refEndpoint: '/master-data/departments', refLabel: 'name',
      sortField: 'department',
      render: (r: any) => r.department?.name || '—' },
    { key: 'createPortalLogin', label: 'Portal', filterType: 'boolean',
      render: (r: any) => r.createPortalLogin
        ? <Badge variant="success">Enabled</Badge>
        : <span className="text-muted-foreground">—</span> },
    { key: 'status', label: 'Status', filterType: 'enum',
      enumOptions: [
        { value: 'Active', label: 'Active' },
        { value: 'Archived', label: 'Archived' },
      ],
      render: (r: any) => <Badge variant={r.status === 'Active' ? 'success' : 'secondary'}>{r.status}</Badge> },
  ];

  const config: CrudConfig = {
    title: 'Employees',
    singular: 'Employee',
    endpoint: '/employees',
    formSlug: 'employees',
    pageSlug: 'employees_page',
    createOverride: openNew,
    editOverride: openEdit,
    // Status filter — lifecycle tabs. "Active" is the default for new visits.
    tabs: [
      { label: 'Active',   filter: { status: 'Active' } },
      { label: 'Archived', filter: { status: 'Archived' } },
      { label: 'All' },
    ],
    columns,
    // `fields` is intentionally NOT used for create / edit — those go
    // through `createOverride` / `editOverride` above which render the
    // custom Employee form. But the read-only **DetailDialog** that
    // opens on row-click still reads this array to lay out the detail
    // view; leaving it empty rendered a blank dialog. The list below
    // mirrors what the custom form already collects so the detail view
    // shows the same picture an edit would.
    fields: [
      { name: 'fullName', label: 'Full Name', type: 'text' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'mobile', label: 'Mobile', type: 'text' },
      { name: 'rolePermission', label: 'Role', type: 'ref',
        refEndpoint: '/roles', refLabel: 'name' },
      { name: 'department', label: 'Department', type: 'ref',
        refEndpoint: '/master-data/departments', refLabel: 'name' },
      { name: 'serviceLocation', label: 'Service Location', type: 'ref',
        refEndpoint: '/master-data/service-locations', refLabel: 'name' },
      { name: 'branches', label: 'Branches', type: 'multi-ref',
        refEndpoint: '/master-data/branches', refLabel: 'name' },
      { name: 'hourlyRate', label: 'Hourly Rate', type: 'number' },
      { name: 'createPortalLogin', label: 'Portal Login Enabled', type: 'boolean' },
      { name: 'jobcardNotificationEmail', label: 'Job Card Notifications', type: 'boolean' },
      { name: 'emailTipperFleetRunSheet', label: 'Tipper Fleet Run Sheet Emails', type: 'boolean' },
      { name: 'status', label: 'Status', type: 'select', options: [
        { value: 'Active', label: 'Active' },
        { value: 'Archived', label: 'Archived' },
      ] },
    ],
    bulkActions: [
      {
        label: 'Allow Portal Login',
        actionKey: 'employee.invite-portal',
        onClick: async (ids, { clearSelection }) => {
          if (!(await confirm({ title: 'Send portal login invite?', description: 'For the selected employees. Records without an email are skipped. Employees who already have portal access will receive a fresh invite (useful when the previous one expired).', confirmLabel: 'Send' }))) return;
          bulkInvitePortal.mutate(ids, { onSuccess: () => clearSelection() });
        },
      },
      {
        label: 'Assign Department',
        actionKey: 'employee.assign-department',
        onClick: (ids) => { setPickedDeptId(''); setAssignDeptDialog({ ids }); },
      },
      {
        label: 'Unassign Department',
        actionKey: 'employee.unassign-department',
        onClick: async (ids, { clearSelection }) => {
          if (!(await confirm({ title: `Clear department on ${ids.length} employee(s)?`, confirmLabel: 'Clear' }))) return;
          unassignDept.mutate(ids, { onSuccess: () => clearSelection() });
        },
      },
      {
        label: 'Deactivate',
        actionKey: 'employee.deactivate',
        variant: 'destructive',
        onClick: async (ids, { clearSelection }) => {
          if (!(await confirm({ title: `Deactivate ${ids.length} employee(s)?`, description: 'Records without portal login will be skipped.', destructive: true, confirmLabel: 'Deactivate' }))) return;
          deactivate.mutate(ids, { onSuccess: () => clearSelection() });
        },
      },
      {
        label: 'Reactivate',
        actionKey: 'employee.reactivate',
        onClick: async (ids, { clearSelection }) => {
          if (!(await confirm({ title: `Reactivate ${ids.length} employee(s)?`, description: 'Sets status to Active. Portal login stays off — invite separately if needed.', confirmLabel: 'Reactivate' }))) return;
          reactivate.mutate(ids, { onSuccess: () => clearSelection() });
        },
      },
    ],
    rowActions: [
      {
        label: 'Allow Portal Login',
        actionKey: 'employee.invite-portal',
        onClick: (row) => invitePortal.mutate(row.id),
        visible: (r) => !!r.email && !r.createPortalLogin,
      },
      {
        // Resend path — same endpoint and same permission gate; the
        // server's /invite-portal handler always re-sends regardless
        // of current state, so a distinct label is the only thing
        // that changes for already-enabled employees. Useful when the
        // first invite expired or the user lost the email.
        label: 'Resend Portal Invite',
        actionKey: 'employee.invite-portal',
        onClick: (row) => invitePortal.mutate(row.id),
        visible: (r) => !!r.email && !!r.createPortalLogin && r.status === 'Active',
      },
      {
        label: 'Assign Department',
        actionKey: 'employee.assign-department',
        onClick: (row) => { setPickedDeptId(row.department?.id ?? ''); setAssignDeptDialog({ ids: [row.id] }); },
      },
      {
        label: 'Unassign Department',
        actionKey: 'employee.unassign-department',
        onClick: async (row) => {
          if (!(await confirm({ title: 'Clear department on this employee?', confirmLabel: 'Clear' }))) return;
          unassignDept.mutate([row.id]);
        },
        visible: (r) => !!r.department,
      },
      {
        label: 'Deactivate',
        actionKey: 'employee.deactivate',
        variant: 'destructive',
        onClick: async (row) => {
          if (!(await confirm({ title: 'Deactivate this employee?', description: 'They will be archived and lose portal login.', destructive: true, confirmLabel: 'Deactivate' }))) return;
          deactivate.mutate([row.id]);
        },
        visible: (r) => r.createPortalLogin && r.status === 'Active',
      },
      {
        label: 'Reactivate',
        actionKey: 'employee.reactivate',
        onClick: async (row) => {
          if (!(await confirm({ title: 'Reactivate this employee?', description: 'Sets status to Active. Portal login stays off — invite separately if needed.', confirmLabel: 'Reactivate' }))) return;
          reactivate.mutate([row.id]);
        },
        visible: (r) => r.status !== 'Active',
      },
    ],
  };

  // ---- Render ----
  // While the inline form is open, hide the table and show the form Card.
  // CrudPage doesn't render inside this branch so the user gets a focused
  // edit view (same UX as before the migration).
  if (open) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button size="icon" variant="ghost" onClick={() => setOpen(false)} aria-label="Back to list">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-semibold truncate">{editing ? 'Edit Employee' : 'New Employee'}</h1>
        </div>
        <Card>
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input value={form.name?.firstName ?? ''} onChange={(e) => setForm({ ...form, name: { ...form.name, firstName: e.target.value } })} />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={form.name?.lastName ?? ''} onChange={(e) => setForm({ ...form, name: { ...form.name, lastName: e.target.value } })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input value={form.mobile ?? ''} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Hourly Rate</Label>
              <Input type="number" value={form.hourlyRate ?? 0} onChange={(e) => setForm({ ...form, hourlyRate: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Role (Permission)</Label>
              <LookupField
                endpoint="/roles"
                labelKey="name"
                value={form.rolePermission || null}
                onChange={(id) => setForm({ ...form, rolePermission: id ?? null })}
                placeholder="— None —"
              />
            </div>
            <div>
              <Label>Department</Label>
              <LookupField
                endpoint="/master-data/departments"
                labelKey="name"
                value={form.department || null}
                onChange={(id) => setForm({ ...form, department: id ?? null })}
                placeholder="— None —"
              />
            </div>
            <div>
              <Label>Service Location</Label>
              <LookupField
                endpoint="/master-data/service-locations"
                labelKey="name"
                value={form.serviceLocation || null}
                onChange={(id) => setForm({ ...form, serviceLocation: id ?? null })}
                placeholder="— None —"
              />
            </div>
            <div>
              <Label className="mb-2 block">Branches</Label>
              <div className="grid grid-cols-2 gap-2 border rounded-md p-3 max-h-48 overflow-y-auto">
                {branches.map((b: any) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.branches?.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} />
                    {b.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              {(() => {
                const hasEmail = !!(form.email ?? '').trim();
                const portalDisabled = !canInvitePortal || !hasEmail;
                let portalReason = '';
                if (!canInvitePortal) portalReason = 'You don\'t have permission to invite users to portal access.';
                else if (!hasEmail) portalReason = 'An email address is required to enable portal login.';
                return (
                  <label className={`flex items-center gap-2 text-sm ${portalDisabled ? 'opacity-60' : ''}`} title={portalReason}>
                    <Checkbox
                      checked={!!form.createPortalLogin}
                      disabled={portalDisabled}
                      onCheckedChange={(v) => setForm({ ...form, createPortalLogin: !!v })}
                    />
                    Create portal login
                    {portalReason && (
                      <span className="text-xs text-muted-foreground">({portalReason})</span>
                    )}
                  </label>
                );
              })()}
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!form.jobcardNotificationEmail} onCheckedChange={(v) => setForm({ ...form, jobcardNotificationEmail: !!v })} />
                Receive job-card notifications
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!form.emailTipperFleetRunSheet} onCheckedChange={(v) => setForm({ ...form, emailTipperFleetRunSheet: !!v })} />
                Email tipper fleet run sheet
              </label>
            </div>
            <div className="flex flex-wrap gap-2 pt-4 border-t justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={save.isPending}>{editing ? 'Update' : 'Create'}</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <CrudPage config={config} />

      <Dialog open={!!assignDeptDialog} onOpenChange={(o) => { if (!o) { setAssignDeptDialog(null); setPickedDeptId(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assign Department</DialogTitle></DialogHeader>
          <div className="py-2 space-y-2">
            <Label>Department</Label>
            <LookupField
              endpoint="/master-data/departments"
              labelKey="name"
              value={pickedDeptId || null}
              onChange={(id) => setPickedDeptId(id ?? '')}
              placeholder="— Select —"
              required
            />
            <p className="text-xs text-muted-foreground pt-2">
              Applied to {assignDeptDialog?.ids.length} employee(s).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignDeptDialog(null); setPickedDeptId(''); }}>Cancel</Button>
            <Button
              disabled={!pickedDeptId || assignDept.isPending}
              onClick={() => assignDept.mutate({ ids: assignDeptDialog!.ids, departmentId: pickedDeptId })}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
